// ===== Constants =====

import {
    MILLISECONDS_IN_AN_HOUR,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_A_SECOND,
}                               from '../constants/generalConstants';

const padTimestampSegment = (value: number): string => value.toString().padStart(2, '0');

/**
 * Audio timestamp displays appear in both the inline note UI and the floating
 * reader mini-player. Keep the formatting logic in one place so those surfaces
 * stay in sync while still allowing small presentation differences.
 */
const formatAudioTimestamp = ({
    timestampMs,
    playbackSpeed = 1,
    padHours = false,
    clampNegativeToZero = false,
}: {
    timestampMs: number,
    playbackSpeed?: number,
    padHours?: boolean,
    clampNegativeToZero?: boolean,
}): string => {
    if (!Number.isFinite(timestampMs)) {
        return '00:00';
    }

    const resolvedPlaybackSpeed = (
        Number.isFinite(playbackSpeed)
        && playbackSpeed > 0
    )
        ? playbackSpeed
        : 1;
    const adjustedTimestampMs = timestampMs / resolvedPlaybackSpeed;
    if (clampNegativeToZero && adjustedTimestampMs < 0) {
        return '00:00';
    }

    const isNegative = adjustedTimestampMs < 0;
    const absoluteTimestampMs = Math.abs(adjustedTimestampMs);
    const hours = Math.floor(absoluteTimestampMs / MILLISECONDS_IN_AN_HOUR);
    const minutes = Math.floor(
        (absoluteTimestampMs % MILLISECONDS_IN_AN_HOUR) / MILLISECONDS_IN_A_MINUTE,
    );
    const seconds = Math.floor(
        (absoluteTimestampMs % MILLISECONDS_IN_A_MINUTE) / MILLISECONDS_IN_A_SECOND,
    );
    const sign = isNegative ? '-' : '';

    if (hours > 0) {
        const hoursText = padHours
            ? padTimestampSegment(hours)
            : `${hours}`;

        return `${sign}${hoursText}:${padTimestampSegment(minutes)}:${padTimestampSegment(seconds)}`;
    }

    return `${sign}${padTimestampSegment(minutes)}:${padTimestampSegment(seconds)}`;
};

export default formatAudioTimestamp;
